import React from 'react'
import {push, replace} from 'connected-react-router'
import cx from 'classnames'

/* Constants */
import {TAB_IDS} from './productDesktopConstants'
import {DAVINCI_EVENT_NAMES, DAVINCI_PAGE_TYPES} from '../analytics/davinci/davinciConstants'
import {HBUS_CLIENT_TYPES} from '../analytics/hbus/hbusConstants'
import {FEATURE_TOGGLE_NAMES} from '../feature-toggle/featureToggleConstants'
import {
  DESKTOP_COMPONENTS,
  DESKTOP_COMPONENTS_OPTIONS,
} from '../voltran/voltranConstants'

/* Components */
import VoltranFragment from '../../components/VoltranFragment/VoltranFragment'
import TechSpecs from '../../views/product/desktop/tech-specs/TechSpecs'

/* Config */
import appConfig from '../../appConfig'

/* Actions */
import {actionCreators as analyticsActionCreators} from '../../redux/actions/analyticsActions'

/* Utils */
import {getBrowserCookie} from '../../../client/common/persistence/browserCookieUtils'
import {generateComparedProductsForPdp} from './generateComparedProductsForPdp'
import {generateProductStatus} from './productCommonUtils'
import leoProductEvents from '../../events/leo/productEvents';

/* Styles */
import s from '../../routes/desktop/product-detail/product-detail.scss'

function formatPrice(price, stripDecimals = false) {
  if (isNaN(price)) {
    return null;
  }

  const formatter = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formattedPrice = formatter.format(price);

  if (stripDecimals && formattedPrice.endsWith(',00')) {
    return `${formattedPrice.replace(/,00$/, '')} TL`;
  }

  return `${formattedPrice} TL`;
}

function generateCommonProps(
  {
    dispatch,
    product,
    pageTypeForDavinci,
    componentData,
    position,
    queryParams,
    fragmentsMap,
    pageFeatureToggleMap,
    userInfo,
    activeVariant,
    isAvailableProduct,
    tabActiveIndex,
    isBot,
    isPremium,
    loyaltyInfoPending
  }) {
  return {
    dispatch,
    position,
    sku: product?.sku,
    name: product?.name,
    brandName: product?.brand,
    brandUrl: product?.brandId,
    merchant: product?.merchant,
    merchantName: product?.merchant?.name,
    merchantUrl: product?.merchant?.urlPostfix,
    merchantId: product?.merchant?.id,
    productId: product?.productId,
    listingId: product?.listingId,
    isInStock: product?.isInStock,
    activeVariant: activeVariant,
    isPreOrder: product?.isPreOrder,
    inStockDate: product?.inStockDate,
    isBundle: product?.isBundle,
    isClosedProduct: product?.isClosedProduct,
    isAvailableProduct: isAvailableProduct,
    categories: product?.categories,
    categoryName: product?.categories?.[product?.categories.length - 1]?.categoryName,
    categoryId: product?.categories?.[product?.categories.length - 1]?.categoryId,
    categoryUrlKeyword: product?.categories?.[product?.categories.length - 1]?.urlKeyword,
    pageTypeForDavinci: pageTypeForDavinci,
    price: getProductFinalPrice(product?.prices),
    componentData: componentData,
    availableStock: product?.stockInformation?.availableStock,
    queryParams,
    fragmentsMap,
    pageFeatureToggleMap,
    userInfo,
    tabActiveIndex,
    isBot,
    isPremium,
    loyaltyInfoPending
  }
}

function generateProductDetailCarouselProps(
  {
    dispatch,
    position,
    product,
    pageTypeForDavinci,
    isAdultProduct,
    pageFeatureToggleMap,
    componentData,
    isAvailableProduct,
    queryParams,
    isPremium,
    loyaltyInfoPending
  }) {
  return {
    className: s.carousel,
    ...generateCommonProps({
      dispatch,
      product,
      position,
      componentData,
      pageTypeForDavinci,
      isAvailableProduct,
      queryParams,
      pageFeatureToggleMap,
      isPremium,
      loyaltyInfoPending
    }),
    productAnalytics: product?.productAnalytics,
    imageBadge: product?.imageBadge,
    productMedia: product?.media,
    isAdultProduct: isAdultProduct,
    isFashionProduct: product?.isFashionProduct,
    checker: true
  }
}

function generateSummaryProps(
  {
    dispatch,
    position,
    product,
    pageTypeForDavinci,
    pageFeatureToggleMap,
    componentData,
    queryParams,
    seoInformation,
    energyClassBadge
  }) {
  return {
    className: s.summary,
    name: seoInformation?.h1 || product?.name,
    ...generateCommonProps({
      dispatch,
      product,
      position,
      componentData,
      pageTypeForDavinci,
      queryParams,
      pageFeatureToggleMap
    }),
    reviews: product?.reviews,
    slugName: product?.slugName,
    h1: seoInformation?.h1,
    energyClassBadge: energyClassBadge,
    queryParams,
    checker: true,
  }
}

function generateBuyboxSellerProps(
  {
    dispatch,
    position,
    product,
    pageTypeForDavinci,
    pageFeatureToggleMap,
    componentData,
    queryParams,
    fragmentsMap
  }) {
  return {
    className: s.summary,
    ...generateCommonProps({
      dispatch,
      product,
      position,
      componentData,
      pageTypeForDavinci,
      queryParams,
      pageFeatureToggleMap
    }),
    pageFeatureToggleMap,
    product,
    fragmentsMap,
    checker: true,
  }
}

function generateVariantProps(
  {
    dispatch,
    position,
    product,
    pageTypeForDavinci,
    pageFeatureToggleMap,
    componentData,
    queryParams,
    activeVariant,
    isAvailableProduct
  }) {
  return {
    className: s.variant,
    ...generateCommonProps({
      dispatch,
      product,
      position,
      componentData,
      pageTypeForDavinci,
      queryParams,
      pageFeatureToggleMap,
      activeVariant,
      isAvailableProduct
    }),
    isFashionProduct: product?.isFashionProduct,
    checker: true,
  }
}

function generateAddToCartProps(
  {
    dispatch,
    position,
    product,
    pageTypeForDavinci,
    pageFeatureToggleMap,
    componentData,
    queryParams,
    isAvailableProduct,
    selectedVasProduct,
    fragmentsMap
  }) {
  return {
    className: s.addToCart,
    ...generateCommonProps({
      dispatch,
      product,
      position,
      componentData,
      pageTypeForDavinci,
      queryParams,
      pageFeatureToggleMap,
      isAvailableProduct,
      fragmentsMap
    }),
    listings: product?.listings || [],
    imageUrl: product?.media?.[0]?.url,
    customerReviewCount: product?.reviews?.customerReviewCount,
    customerReviewScore: product?.reviews?.customerReviewScore,
    isPreOrder: product?.isPreOrder,
    isInStock: product?.isInStock,
    productStatus: generateProductStatus(product),
    selectedVasProduct,
    checker: true,
  }
}

function generateRecommendationProps(
  {
    dispatch,
    product,
    position,
    pageTypeForDavinci,
    componentData,
    queryParams,
    fragmentsMap,
    userInfo,
    isAvailableProduct,
    isBot,
  }) {
  return {
    className: s.recommendation,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    userInfo,
    fragmentsMap,
    isAvailableProduct,
    checker: true
  }
}

function generateSponsoredProductsProps(
  {
    dispatch,
    product,
    position,
    pageTypeForDavinci,
    componentData,
    queryParams,
    fragmentsMap,
  }) {
  return {
    className: s.sponsoredProducts,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    fragmentInfo: fragmentsMap?.get(DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.SPONSORED_PRODUCTS]?.id?.toString()),
    options: DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.SPONSORED_PRODUCTS],
    checker: true,
  }
}

function generateMerchantCouponsProps(
  {
    dispatch,
    product,
    position,
    pageTypeForDavinci,
    componentData,
    queryParams,
    pageFeatureToggleMap,
    isAvailableProduct,
    userInfo
  }) {
  return {
    className: s.merchantCoupons,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, userInfo}),
    checker: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.IS_DISPLAY_MERCHANT_COUPONS_ON_PDP) && isAvailableProduct,
  }
}

function generateCampaignBundleProps(
  {
    dispatch,
    position,
    product,
    pageTypeForDavinci,
    componentData,
    campaignDetail,
    queryParams,
    isAvailableProduct,
    isGetCampaignsPdpDashboardPending,
    campaigns,
    bundleCampaign
  }) {
  return {
    className: s.campaignBundle,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    prepareProduct: {
      sku: product.sku,
      productId: product.productId,
      listingId: product.listingId,
      quantity: 1,
      imageUrl: product.media?.[0]?.url || '',
      name: product.name,
      merchantId: product.merchantId,
      merchantName: product.merchantName,
      price: product?.prices?.[0]?.value || '',
    },
    isGetCampaignsPdpDashboardPending,
    campaigns,
    bundleCampaign,
    checker: isAvailableProduct,
  }
}

function generateKeyFeaturesProps(
  {
    dispatch,
    product,
    position,
    pageTypeForDavinci,
    componentData,
    queryParams,
    energyClassBadge
  }) {
  const count = energyClassBadge ? 3 : 4
  return {
    className: s.keyFeatures,
    ...generateCommonProps({
      dispatch,
      product,
      position,
      componentData,
      pageTypeForDavinci,
      queryParams,
    }),
    techSpecs: product?.expends?.flatMap?.(item => item.properties).slice(0, count) || [],
    shipmentInformation: product?.shipmentInformation,
    product_status: generateProductStatus(product),
    energyClassBadge: energyClassBadge,
    checker: product?.expends?.flatMap?.(item => item.properties)?.length > 0,
  }
}

function generateCampaignsProps(
  {
    dispatch,
    product,
    position,
    pageTypeForDavinci,
    componentData,
    queryParams,
    isAvailableProduct,
    campaigns,
  }) {
  return {
    className: s.campaigns,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    campaigns: campaigns,
    shipmentInformation: product?.shipmentInformation,
    product_status: generateProductStatus(product),
    checker: isAvailableProduct,
  }
}

function generateBuybackRowProps(
  {
    dispatch,
    product,
    position,
    pageTypeForDavinci,
    queryParams,
    buyback,
    componentData,
    fragmentsMap,
    isAvailableProduct
  }) {
  let tbaUrl = '';
  if (buyback?.tbaUrl) {
    tbaUrl = new URL(buyback.tbaUrl)
    tbaUrl?.searchParams?.append('isOpenTbaDirectly', true)
  }

  return {
    className: s.buybackRow,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    fragmentPayload: {
      ...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.BUYBACK],
      paramater: {
        ...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.BUYBACK].paramater,
      },
      clientUrl: tbaUrl?.href,
    },
    buyback,
    fragmentInfo: fragmentsMap?.get(DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.BUYBACK]?.id?.toString()),
    options: DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.BUYBACK],
    renderMethodClientSide: process.env.BROWSER,
    checker: buyback && isAvailableProduct,
  }
}

function generateSizeReviewProps(
  {
    dispatch,
    product,
    position,
    pageTypeForDavinci,
    componentData,
    queryParams,
    isAvailableProduct,
    fashionReviewSummary
  }) {
  return {
    className: s.sizeReview,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    sizeReviewText: fashionReviewSummary?.text,
    checker: product?.isFashionProduct && isAvailableProduct && fashionReviewSummary?.text
  }
}

function generateOtherMerchantsProps(
  {
    dispatch,
    position,
    product,
    pageTypeForDavinci,
    componentData,
    history,
    queryParams,
    allListings,
    dueDate,
    match,
    isAvailableProduct,
    userInfo
  }) {
  return {
    className: s.otherMerchants,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, userInfo}),
    formattedMinimumPriceOfListing: product?.formattedMinimumPriceOfListing,
    formattedMaximumPriceOfListing: product?.formattedMaximumPriceOfListing,
    isShowPriceRangeOfListings: product?.isShowPriceRangeOfListings,
    listings: product?.listings?.filter(listing => listing?.merchantId !== product?.merchant?.id),
    history,
    match,
    allListings,
    dueDate,
    product,
    hasMoreListings: product?.hasMoreListings,
    productStatus: generateProductStatus(product),
    reviewRate: product?.reviews?.customerReviewScore?.replace?.('.', ',') || '',
    reviewcount: product?.reviews?.customerReviewCount || '',
    imageUrl: product?.media?.[0]?.url,
    checker: product?.listings?.filter(listing => listing?.merchantId !== product?.merchant?.id)?.length > 0 && isAvailableProduct
  }
}

function generatePremiumBannerProps(
  {
    dispatch,
    position,
    pageTypeForDavinci,
    queryParams,
    fragmentsMap,
    componentData,
    pageFeatureToggleMap,
    product,
    userInfo,
    isAvailableProduct,
    isGetCampaignsPdpDashboardPending,
    campaignEvaluateResult
  }) {
  return {
    className: s.premium,
    ...generateCommonProps({
      dispatch,
      product,
      position,
      componentData,
      pageTypeForDavinci,
      queryParams,
      pageFeatureToggleMap,
      fragmentsMap
    }),
    fragmentInfo: fragmentsMap?.get(DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.PREMIUM_BANNER]?.id?.toString()),
    options: DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.PREMIUM_BANNER],
    userId: userInfo?.userId,
    fragmentPayload: {
      ...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.PREMIUM_BANNER],
      paramater: {
        ...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.PREMIUM_BANNER].paramater,
      }
    },
    isGetCampaignsPdpDashboardPending,
    campaignEvaluateResult,
    premiumContentRequestPayload: {
      sku: product?.sku,
      userId: userInfo?.userId,
      definitionId: product?.definitionId,
      finalPrice: !isGetCampaignsPdpDashboardPending && campaignEvaluateResult?.evaluateAsPremiumResult?.discountedAmount || campaignEvaluateResult?.evaluateResult?.discountedAmount || getProductFinalPrice(product?.prices),
      merchantId: product?.merchant?.id,
      isMobile: false,
    },
    isVisible: product?.isInStock && !product?.isPreOrder && product?.isProductLive,
    checker: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.IS_DISPLAY_LOYALTY_PREMIUM_ON_PDP) && isAvailableProduct
  }
}

function generateDeliveryOptionsProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  isLoading,
  queryParams,
  isAvailableProduct,
  dueDate,
  deliveryTooltips,
  fragmentsMap
}) {
  const location = getBrowserCookie('location') && JSON.parse(getBrowserCookie('location'))
  return {
    className: cx(s.deliveryOptions, s[componentData?.displayType]),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, fragmentsMap}),
    isLoading: isLoading,
    dueDate,
    deliveryTooltips,
    location: location,
    product,
    checker: isAvailableProduct && !product?.nonTransferable
  }
}

function generateProductBadgesProps({
  dispatch,
  product,
  position,
  pageTypeForDavinci,
  componentData,
  queryParams,
  isAvailableProduct
}) {
  return {
    className: s.productBadges,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    badges: product?.badges,
    checker: isAvailableProduct && product?.badges?.length > 0,
  }
}

function generatePaymentOptionsProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  fragmentsMap,
  buyback,
  queryParams,
  isLoading,
  isAvailableProduct,
  payments,
  isLoadingPayments,
  pageFeatureToggleMap,
  campaignEvaluateResult,
  campaignEvaluateFinalPrice
}) {
  return {
    className: cx(s.paymentOptions, s[componentData?.displayType]),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    fragmentsMap: fragmentsMap,
    paymentOptions: payments,
    amount: product?.prices?.[product?.prices?.length - 1]?.formattedPrice,
    currentPriceBeforePoint: product?.prices?.[product?.prices?.length - 1]?.formattedPrice?.split(',')[0],
    currentPriceAfterPoint: product?.prices?.[product?.prices?.length - 1]?.formattedPrice?.split(',')[1],
    definitionId: product?.definitionId,
    numberOfInstallments: product?.numberOfInstallments,
    paymentTag: product?.paymentTag,
    campaignEvaluateResult,
    campaignEvaluateFinalPrice,
    consumerFinanceTagList: product?.consumerFinanceTagList,
    buyback: buyback,
    taxVatRate: product?.taxVatRate,
    isLoading: isLoading || isLoadingPayments,
    checker: isAvailableProduct && pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_CARD_AND_INSTALLMENT_INFO_ON_PDP)
  }
}

function generateTabProps(
  {
    dispatch,
    isAvailableProduct,
    position,
    pageTypeForDavinci,
    queryParams,
    fragmentsMap,
    componentData,
    pageFeatureToggleMap,
    product,
    seoInformation,
    match,
    tabActiveIndex,
    campaignEvaluateResult,
    campaignEvaluateFinalPrice
  }) {
  let tabList = [
    {
      id: TAB_IDS.DESCRIPTION,
      title: 'Ürün Açıklaması',
      component: (
        <>
          <VoltranFragment
            fragmentInfo={fragmentsMap?.get(DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.PRODUCT_DESCRIPTION]?.id?.toString())}
            options={DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.PRODUCT_DESCRIPTION]}
            renderMethodClientSide={process.env.BROWSER}
            removeClientStyles/>
          {
            product?.expends?.length > 0 &&
            <TechSpecs
              techSpecs={product.expends} {...generateCommonProps({
                dispatch,
                product,
                position,
                componentData,
                pageTypeForDavinci,
                queryParams,
                tabActiveIndex
              })}/>
          }
        </>
      ),
    }
  ]
  if (pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.CALL_USER_CONTENT_SERVICE_ON_PRODUCT_DETAIL) || pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.CALL_USER_CONTENT_SERVICE_ON_CLIENT_SIDE)) {
    tabList.push({
      id: TAB_IDS.REVIEWS,
      title: 'Değerlendirmeler',
      component: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.CALL_USER_CONTENT_SERVICE_ON_PRODUCT_DETAIL) ? (
        <VoltranFragment
          fragmentInfo={fragmentsMap?.get(DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.USER_CONTENT]?.id?.toString())}
          options={DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.USER_CONTENT]}
          renderMethodClientSide={process.env.BROWSER}
          removeClientStyles/>
      ) : null,
      notification: product?.reviews?.customerReviewCount || null
    })
  }
  if (pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.CALL_QUESTION_ANSWER_TAB_SERVER_SIDE) || pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.CALL_QUESTION_ANSWER_TAB_CLIENT_SIDE)) {
    tabList.push({
      id: TAB_IDS.QUESTION_ANSWER,
      title: 'Soru Cevap',
      notification: fragmentsMap?.get(DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.QUESTION_AND_ANSWER]?.id?.toString())?.seoState?.totalItemCount || null,
      component: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.CALL_QUESTION_ANSWER_TAB_SERVER_SIDE) ? (
        <VoltranFragment
          fragmentInfo={fragmentsMap?.get(DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.QUESTION_AND_ANSWER]?.id?.toString())}
          options={DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.QUESTION_AND_ANSWER]}
          renderMethodClientSide={process.env.BROWSER}
          removeClientStyles/>
      ) : null
    })
  }
  if (pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_SARDES_MFE_INSTALLMENT_TAB)) { //CSR COMPONENT
    tabList.push({
      id: TAB_IDS.INSTALLMENTS,
      title: 'Kredi Kart Taksitleri',
    })
  }
  if (pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_SARDES_MFE_LOAN_TAB)) { //CSR COMPONENT
    tabList.push({
      id: TAB_IDS.LOAN_TAB,
      title: 'Alışveriş Kredisi',
    })
  }

  tabList.push({
    id: TAB_IDS.RETURN_POLICY,
    title: 'İptal ve İade Koşulları',
  })

  return {
    className: s.tab,
    ...generateCommonProps({
      dispatch,
      product,
      position,
      componentData,
      pageTypeForDavinci,
      queryParams,
      tabActiveIndex
    }),
    match,
    tabList,
    product,
    seoInformation,
    campaignEvaluateResult,
    campaignEvaluateFinalPrice,
    enableErrorFeedbackButton: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.ENABLE_ERROR_FEEDBACK_BUTTON),
    currentPriceBeforePoint: product?.prices?.[product?.prices?.length - 1]?.formattedPrice?.split(',')?.[0],
    currentPriceAfterPoint: product?.prices?.[product?.prices?.length - 1]?.formattedPrice?.split(',')?.[1],
    checker: isAvailableProduct
  }
}

function generateMinQuantityProps(
  {
    dispatch,
    product,
    position,
    pageTypeForDavinci,
    queryParams,
    componentData,
    isAvailableProduct
  }) {
  return {
    className: cx(s.minQuantity),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    minimumPurchasableQuantity: product?.minimumPurchasableQuantity,
    checker: product?.minimumPurchasableQuantity > 0 && isAvailableProduct,
  }
}

function generatePriceProps(
  {
    dispatch,
    product,
    position,
    pageTypeForDavinci,
    queryParams,
    componentData,
    userInfo,
    isAvailableProduct,
    campaignEvaluateResult,
    isGetCampaignsPdpDashboardPending,
    isBot,
    isPremium,
    loyaltyInfoPending
  }) {
  return {
    className: cx(s.price),
    ...generateCommonProps({
      dispatch,
      product,
      position,
      componentData,
      pageTypeForDavinci,
      queryParams,
      userInfo,
      isBot,
      isPremium,
      loyaltyInfoPending
    }),
    prices: product?.prices,
    product: product,
    campaignEvaluateResult: campaignEvaluateResult,
    checker: isAvailableProduct,
    isGetCampaignsPdpDashboardPending
  }
}

function generateVasProps(
  {
    dispatch,
    product,
    position,
    pageTypeForDavinci,
    queryParams,
    componentData,
    isAvailableProduct,
    isVasExists,
    vas,
    selectedVasProduct,
    pageFeatureToggleMap,
    isInStock,
    isPreOrder,
    shipmentInformation,
    vasLoading,
  }) {
  return {
    className: cx(s.vas),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    definationName: product.name,
    selectedVasProduct,
    productStatus: generateProductStatus(product),
    shippingType: getShipmentType({
      isInStock,
      isPreOrder,
      shipmentInformation,
    }),
    vas,
    vasLoading,
    checker: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.USE_VAS_SERVICE) && isVasExists && isAvailableProduct,
  }
}

function generatePopulerKeywordProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  popularKeyword,
  queryParams,
  isAvailableProduct,
  isBot
}) {
  return {
    className: cx(s.popularKeywords),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    keywords: popularKeyword,
    checker: popularKeyword?.length > 0 && isAvailableProduct,
  }
}

function generateComparedProductsProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  isBot,
  queryParams,
  comparedProducts,
  pageFeatureToggleMap,
  match,
}) {
  let newComparedProducts = comparedProducts

  if (comparedProducts?.length > 2) {
    newComparedProducts = generateComparedProductsForPdp({
      currentProduct: product,
      comparedProducts,
      currentProductUrl: match?.url,
    })
  }

  return {
    className: cx(s.comparedProductsWrapper),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    products: newComparedProducts,
    title: `${product?.brand} ${product?.name} ile en çok karşılaştırılanlar`,
    categoryId: product?.categories?.[product?.categories.length - 1]?.categoryId,
    brand: product?.brand,
    placeholderClassName: s.comparedProductsPlaceholder,
    pageType: DAVINCI_PAGE_TYPES.PRODUCT_DETAIL_PAGE,
    showLastUpdatedDate: true,
    checker: pageFeatureToggleMap.get(FEATURE_TOGGLE_NAMES.DISPLAY_COMPARED_TOP_SELLER_PRODUCT_PDP),
  }
}

function generateAdsBannerProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  queryParams,
  isAvailableProduct,
  isBot
}) {
  return {
    className: cx(s.adsBanner),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    banners: product?.banners?.find(item => item?.desktop),
    checker: product?.banners?.find(item => item?.desktop) && isAvailableProduct,
  }
}

function generateGreenBandProps(
  {
    dispatch,
    position,
    product,
    pageTypeForDavinci,
    componentData,
    queryParams,
    isAvailableProduct,
    isBot,
    campaignEvaluateResult,
    isGetCampaignsPdpDashboardPending
  }) {
  return {
    className: cx(s.greenBand),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    campaignEvaluateResult,
    isGetCampaignsPdpDashboardPending,
    checker: isAvailableProduct,
  }
}

function setAskToSellerButtonAttributes(product) {
  const element = document.querySelector('cst-asktoseller-button')

  if (element) {
    element.setAttribute('buybox', 'true')
    element.setAttribute('merchantarea', 'true')
    element.setAttribute('merchant', JSON.stringify({
      id: product?.merchantId,
      name: product?.merchantName,
    }));
    element.setAttribute('product', JSON.stringify({
      sku: product?.sku,
      name: product?.name,
    }))
  }
}

function sendVariantChangeEvent(product) {
  let variants = [];
  // eslint-disable-next-line array-callback-return
  product?.priceBaseVariantList?.map((variant, index) => {
    // eslint-disable-next-line array-callback-return
    variant?.properties?.map((property) => {
      if (property?.isSelected) {
        variants.push({name: variant.name, value: property.name, position: index + 1})
      }
    })
  })
  window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.CHANGE_VARIANT, {
    canonical_url: '',
    category: {
      id: product?.categories?.[product?.categories?.length - 1]?.categoryId,
      name: product?.categories?.[product?.categories?.length - 1]?.categoryName,
    },
    category_hierarchy: product?.categories?.map((item) => (
      {id: item?.categoryId, name: item?.categoryName}
    )),
    page_type: DAVINCI_PAGE_TYPES.PRODUCT_DETAIL_PAGE,
    page_value: product?.sku,
    merchant: {name: product?.merchant?.name, id: product?.merchant?.id},
    isMerchantChanged: false,
    isPriceChanged: false,
    location: DAVINCI_PAGE_TYPES.PRODUCT_DETAIL_PAGE,
    variant: variants,
    products: [{
      listing_id: product?.listingId,
      position: 1,
      prices: product?.price?.formattedPrice,
      product_id: product?.productId,
      product_status: '',
      shipping_type: '',
      sku: product?.sku
    }]
  })
}

function generateProductViewEvent(dispatch, product) {
  dispatch(
    analyticsActionCreators.sendProductViewEvent.trigger({
      clientType: HBUS_CLIENT_TYPES.NAVIGATION.SENDBEACON,
      experiments: window?.hepsiBus?.parsers?.getExperiments(),
      canonical_url: '',
      category_hierarchy: product?.categories?.map((item) => (
        {id: item?.categoryId, name: item?.categoryName}
      )),
      merchant: {name: product?.merchant?.name, id: product?.merchant?.id},
      category: {
        id: product?.categories?.[product?.categories?.length - 1]?.categoryId,
        name: product?.categories?.[product?.categories?.length - 1]?.urlKeyword,
      },
      products: [{
        listing_id: product?.listingId,
        position: 1,
        price: getProductFinalPrice(product?.prices),
        product_id: product?.productId,
        product_status: '',
        shipping_type: '',
        sku: product?.sku
      }],
      review_rate: `${product?.reviews?.customerReviewScore || ''}`.replace('.', ','),
      review_count: `${product?.reviews?.customerReviewCount || ''}`,
      layout: '',
    })
  )
  leoProductEvents.sendProductViewEvent({
    product_id: product?.productId,
    price:  getProductFinalPrice(product?.prices),
    discount: '',
    sku: product?.sku,
  });
}

function generateProductModel(product, chatOptions) {
  window.productModel = {
    product: {
      sku: product?.sku,
      name: product?.name,
      productId: product?.productId,
      definitionName: product?.definitionName,
      winnerFreight: product?.winnerFreight,
      listings: product?.listings,
      categories: product?.categories,
      currentListing: {
        listingId: product?.listingId,
        stockInformation: product?.stockInformation,
        shipmentInformation: product?.shipmentInformation,
        shippingProfileId: product?.shippingProfileId,
        inStockDate: product?.stockInformation?.inStockDate,
        jetDeliveryCities: product?.jetDeliveryCities,
        tagList: product?.tagList,
        merchantId: product?.merchant?.id,
        merchantName: product?.merchant?.name,
        merchantCountry: product?.merchant?.country,
        merchantCity: product?.merchant?.city,
        currentPrice: {
          value: product?.prices?.[product?.prices?.length - 1].value,
          currency: product?.prices?.[product?.prices?.length - 1].currency
        },
        prices: product?.prices,
      },
      mediaResources: product?.media || []
    },
    chatOptions
  }
}

function generateMergeUtagData(userInfo, product) {
  window.utagData = {
    ...window.utagData,
    customer_gender_id: userInfo?.gender,
    customer_id: userInfo?.userId,
    customer_login_status: userInfo?.isAuthenticated ? 'logged in' : 'not logged in',
    customer_segments: [],
    mainProduct: {
      listing_id: product?.listingId,
      position: 1,
      price: product?.prices?.[product?.prices?.length - 1]?.formattedPrice,
      product_id: product?.productId,
      product_status: generateProductStatus(product),
      shipping_type: '',
      sku: product?.productId
    }
  }
}

function checkForProductDetailRedirection(responseData, history = null, queryParams) {
  let redirection

  if (!responseData) {
    return null
  }

  if (responseData?.redirection?.url && !process.env.BROWSER) {
    const searchParams = new URLSearchParams();
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        searchParams.append(key, value)
      }
    }
    const queryString = searchParams.toString();
    responseData.redirection.url += responseData.redirection.url.includes('?') ? '&' : '?' + queryString
    redirection = {
      ...responseData.redirection,
    }
  } else if (responseData?.redirection?.url && process.env.BROWSER) {
    return history?.push(responseData?.redirection?.url)
  }

  return redirection
}

function generateCampaignBundleRequest(sku, merchantId) {
  return window?.Campaign?.CAMPAIGN_BUNDLE?.getBundleCampaigns({
    Products: [
      {
        Sku: sku,
        MerchantId: merchantId
      }
    ],
    Platform: 'WebSite'
  })
}

function generatePremiumContentRequest(payload) {
  window?.Loyalty?.fetchAdvantagesByProduct?.(payload)
}

function generateFollowAndCollectRequest(userId, merchantId, sku) {
  return window?.MerchantContent?.FOLLOW_BUTTON?.getComponentParams({
    sku: sku,
    userId: userId || '',
    merchantId: merchantId
  })
}

function getProductFinalPrice(prices) {
  if (!prices?.length) {
    return null
  }

  const finalPriceIndex = prices.length - 1
  return prices[finalPriceIndex]?.value
}

const updateLocation = (newLocation, {isReplace = false} = {}, dispatch) => {
  if (isReplace) {
    dispatch(replace(newLocation))
  } else {
    dispatch(push(newLocation))
  }
}

const sendProductDetailWebtrekkData = (dispatch, userInfo, webtrekkData) => {
  const data = {
    ...JSON.parse(webtrekkData),
    customSessionParameter: {
      4: userInfo.isAuthenticated ? 'Login' : 'Not Login',
    },
  };

  if (data) {
    dispatch(
      analyticsActionCreators.sendWebtrekkEvent.trigger(data)
    )
  }
}

const getShipmentType = ({isInStock, isPreOrder, shipmentInformation}) => {
  let shipmentInfo = '';

  if (shipmentInformation?.fastShipping === true) {
    shipmentInfo = 'super-hizli';
  } else if (!isInStock && !isPreOrder) {
    shipmentInfo = 'stokta-yok';
  } else if (shipmentInformation?.howManyDaysToShipment && shipmentInformation.howManyDaysToShipment > 0) {
    shipmentInfo = shipmentInformation?.howManyDaysToShipment?.toString?.();
  }

  return shipmentInfo
}

const generateDeliveryOptionsQueryModel = (product, listings, userInfo) => {
  const location = getBrowserCookie('location') && JSON.parse(getBrowserCookie('location'));

  const createQueryModelData = (item, isProduct = false) => ({
    sku: product?.sku || '',
    listingId: item?.listingId,
    definitionName: product?.definitionName || '',
    warehouseId: item?.warehouseId || item?.stockInformation?.warehouseId,
    shipmentDay: item?.shipmentDay,
    shippingProfileId: item?.shippingProfileId,
    deci: product?.winnerFreight || null,
    inStockDate: item?.inStockDate || '',
    tags: item?.tagList?.map(tag => tag.tagId) || '',
    isBuyBoxWinner: isProduct || product?.merchantId === item?.merchantId,
    quantity: 1,
    merchantId: item?.merchantId,
    merchantCity: item?.merchantCity || item?.currentListings?.merchantCity || '',
    merchantCountry: item?.merchantCountry || item?.currentListings?.merchantCountry || '',
    shipmentDaysPredictedByHb: isNaN(parseInt(item?.aiBasedShipmentDay)) ? null : parseInt(item?.aiBasedShipmentDay),
    customerId: userInfo?.userId || getBrowserCookie('hbus_anonymousId'),
    availableWarehouses: item?.availableWarehouses || [],
    ...location && item?.jetDeliveryCities?.length > 0 && {
      jetDeliveryCities: item?.jetDeliveryCities
    }
  })

  const queryModelData = [
    createQueryModelData(product, true),
    ...listings?.map(item => createQueryModelData(item)) || []
  ]

  return queryModelData
}

const generateCustomerAddress = (userInfo) => {
  const location = getBrowserCookie('location') && JSON.parse(getBrowserCookie('location'))
  const payload = {
    customerId: userInfo?.userId || '',
    customerLocation: location?.city?.code || '',
    customerCity: location?.city?.name || '',
    customerTown: location?.town?.name || '',
    customerTownCode: location?.town?.code || '',
    customerDistrict: location?.district?.name || '',
    customerDistrictCode: location?.district?.code || '',
    anonymousId: getBrowserCookie('hbus_anonymousId'),
    locationDeliveryUnavailableDays: location?.address?.locationDeliveryUnavailableDays || [],
  }

  if(Number.isInteger(location?.address?.locationType)) {
    payload.locationType = location?.address?.locationType
  }

  return payload
}

const generateCampaignModel = (product, userInfo, platform, appKey) => {
  return {
    customer: {
      id: userInfo?.userId ? userInfo?.userId : getBrowserCookie('hbus_anonymousId'),
      tags: [],
      gsmNumber: null,
      uniqueDeviceId: null
    },
    platform: platform,
    appKey: appKey,
    product: {
      sku: product?.sku,
      productId: product?.productId,
      brand: product?.brand,
      definitionName: product?.definitionName,
      listingId: product?.listingId,
      productName: product?.name,
      merchant: {
        id: product?.merchant?.id,
        name: product?.merchant?.name,
        isCampaignContractAccepted: true
      },
      otherMerchants: product?.listings?.map((listing) => (
        {
          id: listing?.merchantId,
          name: listing?.merchantName,
          isCampaignContractAccepted: true
        }
      )) || [],
      media: {
        linkFormat: product?.media?.[0]?.url,
        sortId: product?.media?.[0]?.showOrder,
        isDefault: true,
        width: product?.media?.[0]?.width,
        height: product?.media?.[0]?.height
      },
      productTags: product?.tagList?.map((tag) => tag?.tagId) || [],
      rootCategoryList: product?.categories?.map((category) => parseInt(category?.categoryId)) || [],
      url: `${appConfig.baseUrl}${window.location.pathname}${window.location.search}`,
      amount: getProductFinalPrice(product?.prices),
      productTagGroupNames: [], //
      campaignIds: product?.campaignIds
    }
  }
}

const generateDsaModel = (product) => {
  let categoryIds = []
  // eslint-disable-next-line array-callback-return
  product.categories.map((category) => {
    categoryIds.push(category?.categoryId)
  })

  return `DSACategoryId/${categoryIds?.map(item => item).join('/')}`
}

export {
  generateProductDetailCarouselProps,
  generateSummaryProps,
  generateBuyboxSellerProps,
  generateVariantProps,
  generateAddToCartProps,
  generateRecommendationProps,
  generateSponsoredProductsProps,
  generateMerchantCouponsProps,
  generateCampaignBundleProps,
  generateKeyFeaturesProps,
  generateCampaignsProps,
  generateBuybackRowProps,
  generateSizeReviewProps,
  generateOtherMerchantsProps,
  generatePremiumBannerProps,
  generateProductBadgesProps,
  generateTabProps,
  generateMinQuantityProps,
  generateDeliveryOptionsProps,
  generatePaymentOptionsProps,
  generateVasProps,
  generatePriceProps,
  generateAdsBannerProps,
  generateGreenBandProps,
  setAskToSellerButtonAttributes,
  sendVariantChangeEvent,
  generateProductModel,
  generateProductViewEvent,
  generateMergeUtagData,
  checkForProductDetailRedirection,
  generateCampaignBundleRequest,
  generatePremiumContentRequest,
  getProductFinalPrice,
  formatPrice,
  updateLocation,
  sendProductDetailWebtrekkData,
  generateFollowAndCollectRequest,
  getShipmentType,
  generateCustomerAddress,
  generateDeliveryOptionsQueryModel,
  generateCampaignModel,
  generatePopulerKeywordProps,
  generateDsaModel,
  generateComparedProductsProps,
}
